import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Brands', path: '/brands' },
    { name: 'Resources', path: '/resources' },
    { name: 'Contact', path: '/contact' },
    { name: 'Partner Portal', path: '/partner-portal' }
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    setIsOpen(false);
  };

  return (
    <nav className="fixed top-0 w-full bg-ewss-navy/95 backdrop-blur-sm border-b border-ewss-cyan/20 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-12 h-12 flex items-center justify-center group-hover:scale-110 transition-transform">
              <img 
                src="https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/auocukq6_logo.png" 
                alt="EWSS Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <div className="text-white font-bold text-xl">
                EWSS <span className="text-ewss-cyan">Automation</span>
              </div>
              <div className="text-ewss-cyan text-xs">30 Years of Logic</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive(link.path)
                    ? 'text-ewss-cyan bg-ewss-cyan/10 border border-ewss-cyan/30'
                    : 'text-gray-300 hover:text-ewss-cyan hover:bg-ewss-cyan/5'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Auth buttons */}
            {user ? (
              <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-700">
                <div className="flex items-center gap-2 px-3 py-2 bg-ewss-cyan/10 rounded-lg border border-ewss-cyan/30">
                  <User size={16} className="text-ewss-cyan" />
                  <span className="text-sm text-white">
                    {user.name}
                    {isAdmin && <span className="ml-1 text-xs text-ewss-cyan">(Admin)</span>}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all flex items-center gap-2"
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="ml-4 px-4 py-2 bg-ewss-cyan text-ewss-navy font-medium text-sm rounded-lg hover:shadow-[0_0_20px_rgba(23,145,255,0.4)] transition-all"
              >
                Login
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-ewss-cyan p-2 hover:bg-ewss-cyan/10 rounded-lg transition-all"
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-ewss-navy border-t border-ewss-cyan/20">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  isActive(link.path)
                    ? 'text-ewss-cyan bg-ewss-cyan/10 border border-ewss-cyan/30'
                    : 'text-gray-300 hover:text-ewss-cyan hover:bg-ewss-cyan/5'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Mobile auth buttons */}
            <div className="pt-4 border-t border-gray-700">
              {user ? (
                <>
                  <div className="px-4 py-3 bg-ewss-cyan/10 rounded-lg border border-ewss-cyan/30 mb-2">
                    <div className="flex items-center gap-2 text-white text-sm">
                      <User size={16} className="text-ewss-cyan" />
                      {user.name}
                      {isAdmin && <span className="text-xs text-ewss-cyan">(Admin)</span>}
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-3 text-sm font-medium text-red-400 bg-red-400/10 rounded-lg transition-all flex items-center justify-center gap-2"
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setIsOpen(false)}
                  className="block w-full px-4 py-3 bg-ewss-cyan text-ewss-navy font-medium text-sm text-center rounded-lg"
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;